import { getSession, isAdminRole } from "../../auth/authService";
import { ensureJourneyDb, getById, listTable, rowsFromIndex } from "../../db";
import { forbidden, unauthorized } from "./errors";

export function resolveActor(session) {
  ensureJourneyDb();
  const current = session || getSession();
  if (!current?.userId) throw unauthorized();

  const sessionRole = current.role;
  let journeyRole = "buyer";
  if (isAdminRole(sessionRole) || sessionRole === "broker") journeyRole = "broker";
  else if (sessionRole === "agent") journeyRole = "agent";
  else if (sessionRole === "seller") journeyRole = "seller";
  else if (sessionRole === "buyer") journeyRole = "buyer";

  const user = getById("users", current.userId);
  if (user?.role_type && ["broker", "agent", "buyer", "seller"].includes(user.role_type)) {
    if (!isAdminRole(sessionRole)) journeyRole = user.role_type;
  }

  return {
    userId: current.userId,
    sessionRole,
    journeyRole,
    name: current.name || user?.name || "",
    email: current.email || user?.email || "",
    isAdmin: isAdminRole(sessionRole),
    isSupervisor: isAdminRole(sessionRole) || journeyRole === "broker",
  };
}

export function canAccessTransaction(actor, transaction) {
  if (!transaction) return false;
  if (actor.isAdmin) return true;
  if (transaction.client_role === "office") {
    if (actor.journeyRole === "broker") return transaction.broker_id === actor.userId;
    return [transaction.broker_id, transaction.agent_id, transaction.client_id].includes(actor.userId);
  }
  if (actor.journeyRole === "broker") return transaction.broker_id === actor.userId;
  if (actor.journeyRole === "agent") return transaction.agent_id === actor.userId;
  if (actor.journeyRole === "buyer" || actor.journeyRole === "seller") {
    return transaction.client_id === actor.userId;
  }
  return false;
}

export function canAccessThread(actor, thread) {
  if (!thread) return false;
  const transaction = getById("transactions", thread.transaction_id);
  if (!canAccessTransaction(actor, transaction)) return false;
  if (actor.isSupervisor || actor.journeyRole !== "agent") return true;
  if (transaction.agent_id === actor.userId) return true;
  const messages = rowsFromIndex("messages", "messagesByThreadId", thread.id);
  return messages.some((row) => row.sender_id === actor.userId || row.receiver_id === actor.userId);
}

export function assertTransactionAccess(actor, transaction) {
  if (!canAccessTransaction(actor, transaction)) throw forbidden("No access to this transaction");
  return transaction;
}

export function assertThreadAccess(actor, thread) {
  if (!canAccessThread(actor, thread)) throw forbidden("No access to this thread");
  return thread;
}

export function canExportTransaction(actor, transaction) {
  return canAccessTransaction(actor, transaction) && (actor.isSupervisor || actor.journeyRole === "agent");
}

export function canArchiveTransaction(actor, transaction) {
  return canExportTransaction(actor, transaction);
}

export function exportTypeForActor(actor) {
  if (actor.isSupervisor) return "broker";
  if (actor.journeyRole === "agent") return "agent";
  return "client";
}

export function listAccessibleTransactions(session) {
  const actor = resolveActor(session);
  return listTable("transactions").filter((transaction) => canAccessTransaction(actor, transaction));
}

export function counterpartId(transaction, actor) {
  if (transaction?.client_role === "office") {
    if (actor.userId === transaction.agent_id) return transaction.client_id;
    if (actor.userId === transaction.client_id) return transaction.agent_id;
    return transaction.agent_id;
  }
  if (actor.journeyRole === "buyer" || actor.journeyRole === "seller") return transaction.agent_id;
  return transaction.client_id;
}
